/* Anonymous collection discovery, powered by the existing Visual Search index. */
(function () {
  'use strict';

  var PROFILE_KEY = 'muralls:discovery:profile:v1';
  var CACHE_KEY = 'muralls:discovery:similar:v2';
  var MIX_KEY = 'muralls:discovery:mix:v1';
  var API_URL = 'https://wallview-muralls.muralls.workers.dev/api/product-related';
  var CACHE_TTL = 7 * 24 * 60 * 60 * 1000;
  var SEEN_TTL = 90 * 24 * 60 * 60 * 1000;
  var MAX_INTERESTS = 12;
  var MAX_SEEN = 600;
  var warmedThisPage = false;
  var seenObserver = null;
  var pageProduct = null;
  var colorwayRequests = new Map();
  var colorwayQueue = [];
  var activeColorwayRequests = 0;

  function storage() {
    try { return window.localStorage; } catch (error) { return null; }
  }

  function readJson(key, fallback) {
    var target = storage();
    if (!target) return fallback;
    try { return JSON.parse(target.getItem(key) || 'null') || fallback; } catch (error) { return fallback; }
  }

  function writeJson(key, value) {
    var target = storage();
    if (!target) return;
    try { target.setItem(key, JSON.stringify(value)); } catch (error) {}
  }

  function cleanProfile() {
    var now = Date.now();
    var profile = readJson(PROFILE_KEY, { interests: [], seen: {} });
    profile.interests = Array.isArray(profile.interests) ? profile.interests.filter(function (item) {
      return item && item.handle && item.image;
    }).slice(0, MAX_INTERESTS) : [];
    profile.seen = profile.seen && typeof profile.seen === 'object' ? profile.seen : {};
    Object.keys(profile.seen).forEach(function (handle) {
      if (now - Number(profile.seen[handle] || 0) > SEEN_TTL) delete profile.seen[handle];
    });
    var seenHandles = Object.keys(profile.seen);
    if (seenHandles.length > MAX_SEEN) {
      seenHandles.sort(function (a, b) { return profile.seen[b] - profile.seen[a]; });
      seenHandles.slice(MAX_SEEN).forEach(function (handle) { delete profile.seen[handle]; });
    }
    return profile;
  }

  function productFromElement(element) {
    if (!element) return null;
    var data = element.dataset || {};
    var image = data.discoveryReferenceImage || data.visualSearchImage || '';
    var handle = data.discoveryProductHandle || data.visualSearchHandle || '';
    if (!handle || !image) return null;
    return {
      id: String(data.discoveryProductId || ''),
      handle: String(handle),
      image: String(image),
      title: String(data.discoveryProductTitle || data.visualSearchTitle || ''),
      productType: String(data.discoveryProductType || data.visualSearchType || ''),
    };
  }

  function collectionProductFromElement(element) {
    if (!element) return null;
    var data = element.dataset || {};
    var imageElement = element.querySelector('.card__media .media img:first-of-type, .card__media img:first-of-type, img');
    var linkElement = element.querySelector('a[href*="/products/"]');
    var handle = String(data.discoveryProductHandle || '');
    // The blank/reference asset is ideal for embedding lookup, but collection
    // cards should display the same first lifestyle image customers see below.
    // Keep the lifestyle source even before its viewport request finishes.
    var image = String(imageElement?.dataset.viewportSrc || imageElement?.src || imageElement?.currentSrc || data.discoveryReferenceImage || '');
    if (!handle || !image) return null;
    return {
      id: String(data.discoveryProductId || ''),
      handle: handle,
      image: image,
      patternImage: String(data.discoveryReferenceImage || ''),
      title: String(data.discoveryProductTitle || imageElement?.alt || ''),
      productType: String(data.discoveryProductType || ''),
      url: linkElement?.href || '/products/' + encodeURIComponent(handle),
      colorwaysMarkup: element.querySelector('card-colorways')?.outerHTML || '',
    };
  }

  function rememberInterest(product, strength) {
    if (!product || !product.handle || !product.image) return;
    var profile = cleanProfile();
    var existing = profile.interests.find(function (item) { return item.handle === product.handle; });
    profile.interests = profile.interests.filter(function (item) { return item.handle !== product.handle; });
    profile.interests.unshift({
      id: product.id || existing?.id || '',
      handle: product.handle,
      image: product.image,
      title: product.title || existing?.title || '',
      productType: product.productType || existing?.productType || '',
      weight: Math.min(12, Number(existing?.weight || 0) + Number(strength || 1)),
      at: Date.now(),
    });
    profile.interests = profile.interests.slice(0, MAX_INTERESTS);
    writeJson(PROFILE_KEY, profile);
  }

  function rememberSeen(handle) {
    if (!handle) return;
    var profile = cleanProfile();
    profile.seen[handle] = Date.now();
    writeJson(PROFILE_KEY, profile);
  }

  function cacheStore() {
    var cache = readJson(CACHE_KEY, {});
    var now = Date.now();
    Object.keys(cache).forEach(function (handle) {
      if (!cache[handle] || now - Number(cache[handle].at || 0) > CACHE_TTL) delete cache[handle];
    });
    return cache;
  }

  function hasCachedSimilar(product) {
    if (!product || !product.handle) return false;
    var current = cacheStore()[product.handle];
    return Boolean(current && Array.isArray(current.products) && current.products.some(function (item) {
      return item && item.handle && item.image && item.title;
    }));
  }

  function warmSimilar(product) {
    if (!product || !product.id || !product.productType || warmedThisPage) return Promise.resolve();
    var cache = cacheStore();
    var current = cache[product.handle];
    if (current && hasCachedSimilar(product) && Date.now() - Number(current.at || 0) < CACHE_TTL) return Promise.resolve();
    warmedThisPage = true;
    var endpoint = new URL(API_URL);
    endpoint.searchParams.set('product_id', product.id);
    endpoint.searchParams.set('product_type', product.productType);
    endpoint.searchParams.set('limit', '12');
    return fetch(endpoint.toString()).then(function (response) {
      if (!response.ok) throw new Error('discovery_search_' + response.status);
      return response.json();
    }).then(function (payload) {
      var products = Array.isArray(payload.products) ? payload.products : [];
      cache = cacheStore();
      cache[product.handle] = {
        at: Date.now(),
        products: products.slice(0, 36).map(function (item, index) {
          return {
            id: String(item.id || ''),
            handle: String(item.handle || ''),
            title: String(item.title || ''),
            image: String(item.image || ''),
            url: String(item.url || ''),
            score: Number(item.score || 0),
            rank: index + 1,
            productType: String(item.productType || ''),
          };
        }),
      };
      writeJson(CACHE_KEY, cache);
    }).catch(function (error) {
      warmedThisPage = false;
      console.warn('Muralls discovery could not prepare recommendations:', error);
    });
  }

  function hash(text) {
    var value = 2166136261;
    for (var index = 0; index < text.length; index += 1) {
      value ^= text.charCodeAt(index);
      value = Math.imul(value, 16777619);
    }
    return value >>> 0;
  }

  function currentSeed(extra) {
    var visitor = '';
    try { visitor = storage()?.getItem('muralls:funnel:visitor') || ''; } catch (error) {}
    var day = new Date().toISOString().slice(0, 10);
    return hash(visitor + ':' + day + ':' + location.pathname + ':' + String(extra || 0));
  }

  function hasManualCollectionState() {
    var params = new URLSearchParams(location.search);
    if (Number(params.get('page') || 1) > 1 || params.has('sort_by')) return true;
    return Array.from(params.keys()).some(function (key) { return key.indexOf('filter.') === 0; });
  }

  function relevanceMap(profile, cache) {
    var result = new Map();
    profile.interests.slice(0, 4).forEach(function (interest, interestIndex) {
      var cached = cache[interest.handle];
      if (!cached || !Array.isArray(cached.products)) return;
      var interestWeight = Math.max(1, Number(interest.weight || 1)) / (interestIndex + 1);
      cached.products.forEach(function (product) {
        if (!product.handle) return;
        var rankValue = Math.max(0, 40 - Number(product.rank || 40)) / 40;
        var scoreValue = Math.max(0, Number(product.score || 0) - 0.68);
        result.set(product.handle, Number(result.get(product.handle) || 0) + interestWeight * (rankValue + scoreValue * 2));
      });
    });
    return result;
  }

  function recommendationPool(profile, cache, mix, preferredType, excludedHandles, collectionProducts, allowCatalogMatches) {
    var products = new Map();
    var excluded = new Set(Array.isArray(excludedHandles) ? excludedHandles : []);
    var contextualProducts = new Map();
    (Array.isArray(collectionProducts) ? collectionProducts : []).forEach(function (product) {
      if (product && product.handle) contextualProducts.set(product.handle, product);
    });
    var hasCollectionContext = contextualProducts.size > 0;
    var seed = currentSeed(mix);
    profile.interests.slice(0, 4).forEach(function (interest, interestIndex) {
      var cached = cache[interest.handle];
      if (!cached || !Array.isArray(cached.products)) return;
      var interestWeight = Math.max(1, Number(interest.weight || 1)) / (interestIndex + 1);
      cached.products.forEach(function (product) {
        if (!product.handle || !product.image || !product.title) return;
        // Specific collections stay inside their visible context. Broad discovery
        // collections may use the complete precomputed catalog result instead of
        // throwing away every match that is not present on pagination page one.
        if (hasCollectionContext && !allowCatalogMatches && !contextualProducts.has(product.handle)) return;
        if (preferredType && String(product.productType || '').toLowerCase() !== preferredType.toLowerCase()) return;
        var rankValue = Math.max(0.08, 40 - Number(product.rank || 40)) / 40;
        var scoreValue = Math.max(0, Number(product.score || 0) - 0.68);
        var contextualProduct = contextualProducts.get(product.handle);
        var existing = products.get(product.handle) || Object.assign({}, product, contextualProduct || {}, { relevance: 0 });
        existing.relevance += interestWeight * (rankValue + scoreValue * 2);
        existing.explore = hash(product.handle + ':' + seed);
        existing.seen = Boolean(profile.seen[product.handle]);
        products.set(product.handle, existing);
      });
    });

    // Collection context always wins over the global taste profile. Fill the
    // recommendation pool from the open collection so a category change never
    // carries unrelated products into the For You panel.
    if (hasCollectionContext) {
      var contextualScores = relevanceMap(profile, cache);
      contextualProducts.forEach(function (product) {
        var existing = products.get(product.handle) || Object.assign({}, product, { relevance: 0 });
        existing.relevance = Math.max(Number(existing.relevance || 0), Number(contextualScores.get(product.handle) || 0));
        existing.explore = hash(product.handle + ':' + seed);
        existing.seen = Boolean(profile.seen[product.handle]);
        products.set(product.handle, existing);
      });
    }

    var ranked = Array.from(products.values()).sort(function (a, b) {
      if (a.seen !== b.seen) return a.seen ? 1 : -1;
      return b.relevance - a.relevance || a.explore - b.explore;
    });
    if (ranked.length <= 4) return ranked;

    // Keep every remix relevant while moving to a completely different set.
    // Limiting the rotation to the strongest matches prevents later clicks
    // from drifting into visually unrelated products.
    var strongestCandidates = ranked.slice(0, Math.min(24, ranked.length));
    var candidates = strongestCandidates.filter(function (product) {
      return !excluded.has(product.handle);
    });
    if (candidates.length < 4) candidates = strongestCandidates;
    var pageSize = 4;
    var pageCount = Math.ceil(candidates.length / pageSize);
    var page = Math.abs(Number(mix || 0)) % pageCount;
    var start = page * pageSize;
    var result = candidates.slice(start, start + pageSize);

    if (result.length < pageSize) {
      result = result.concat(candidates.slice(0, pageSize - result.length));
    }
    return result;
  }

  function composeOrder(items, profile, mix) {
    var scores = relevanceMap(profile, cacheStore());
    var seed = currentSeed(mix);
    var enriched = items.map(function (item, index) {
      var handle = item.dataset.discoveryProductHandle || '';
      return {
        element: item,
        handle: handle,
        original: index,
        relevant: Number(scores.get(handle) || 0),
        seen: Boolean(profile.seen[handle]),
        explore: hash(handle + ':' + seed),
      };
    });
    var hasRelevant = enriched.some(function (item) { return item.relevant > 0; });
    if (!hasRelevant && !profile.interests.length) return null;
    if (!hasRelevant) {
      return enriched.sort(function (a, b) {
        if (a.seen !== b.seen) return a.seen ? 1 : -1;
        return a.explore - b.explore;
      }).map(function (item) { return item.element; });
    }

    var relevant = enriched.filter(function (item) { return item.relevant > 0; }).sort(function (a, b) {
      if (a.seen !== b.seen) return a.seen ? 1 : -1;
      return b.relevant - a.relevant || a.explore - b.explore;
    });
    var explore = enriched.filter(function (item) { return item.relevant <= 0; }).sort(function (a, b) {
      if (a.seen !== b.seen) return a.seen ? 1 : -1;
      return a.explore - b.explore;
    });
    var ordered = [];
    while (relevant.length || explore.length) {
      for (var count = 0; count < 3 && relevant.length; count += 1) ordered.push(relevant.shift());
      if (explore.length) ordered.push(explore.shift());
      if (!relevant.length) ordered.push.apply(ordered, explore.splice(0));
    }
    return ordered.map(function (item) { return item.element; });
  }

  function restoreOrder(grid) {
    var items = Array.from(grid.querySelectorAll(':scope > [data-discovery-card]'));
    items.sort(function (a, b) { return Number(a.dataset.discoveryOriginal || 0) - Number(b.dataset.discoveryOriginal || 0); });
    items.forEach(function (item, index) {
      item.style.setProperty('--animation-order', String(index + 1));
      grid.appendChild(item);
    });
  }

  function editorialCardTitle(product) {
    var words = String(product.title || '').trim().split(/\s+/).filter(Boolean);
    var title = words.slice(0, 2).join(' ');
    if (title.toLowerCase().indexOf('wallpaper') !== -1) title = words.slice(0, 3).join(' ');
    return { full: title, bold: title, accent: '' };
  }

  function matchingPrice(grid, productType) {
    var sourceCard = Array.from(grid.querySelectorAll(':scope > [data-discovery-card]')).find(function (item) {
      return String(item.dataset.discoveryProductType || '').toLowerCase() === String(productType || '').toLowerCase();
    });
    var sourcePrice = sourceCard && sourceCard.querySelector('.card-price-inline');
    return sourcePrice ? sourcePrice.cloneNode(true) : null;
  }

  function processColorwayQueue() {
    // Visible grid cards already contain their colors. Only off-grid matches
    // need this small Liquid view; limit parallel requests on slower networks.
    while (activeColorwayRequests < 2 && colorwayQueue.length) {
      colorwayQueue.shift()();
    }
  }

  function loadColorways(product) {
    if (typeof product.colorwaysMarkup === 'string') return Promise.resolve(product);
    if (!colorwayRequests.has(product.handle)) {
      var request = new Promise(function (resolve) {
        colorwayQueue.push(function () {
          activeColorwayRequests += 1;
          var controller = new AbortController();
          var timeout = window.setTimeout(function () { controller.abort(); }, 8000);
          var url = new URL('/products/' + encodeURIComponent(product.handle), window.location.origin);
          url.searchParams.set('view', 'card-colorways');
          fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal }).then(function (response) {
            if (!response.ok) throw new Error('discovery_colors_' + response.status);
            return response.text();
          }).then(function (html) {
            var fragment = document.createElement('template');
            fragment.innerHTML = html;
            resolve(fragment.content.querySelector('card-colorways')?.outerHTML || '');
          }).catch(function () {
            colorwayRequests.delete(product.handle);
            resolve('');
          }).finally(function () {
            window.clearTimeout(timeout);
            activeColorwayRequests -= 1;
            processColorwayQueue();
          });
        });
      });
      colorwayRequests.set(product.handle, request);
      processColorwayQueue();
    }
    return colorwayRequests.get(product.handle).then(function (markup) {
      return Object.assign({}, product, { colorwaysMarkup: markup });
    });
  }

  function recommendationCard(product, grid) {
    var card = document.createElement('article');
    card.className = 'collection-discovery__card';
    card.dataset.discoveryRecommendation = product.handle;
    var link = document.createElement('a');
    link.className = 'collection-discovery__card-link';
    link.href = product.url || '/products/' + encodeURIComponent(product.handle);

    var imageWrap = document.createElement('span');
    imageWrap.className = 'collection-discovery__image-wrap';
    var image = document.createElement('img');
    image.className = 'collection-discovery__image';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = product.title;
    var imageSrcset = '';
    // Previously viewed cards may carry a small CDN thumbnail in storage.
    // Let the browser select a sharp source for the new portrait cards.
    try {
      var imageUrl = new URL(product.image, window.location.origin);
      var isShopifyImage = (imageUrl.origin === window.location.origin && imageUrl.pathname.indexOf('/cdn/shop/') === 0)
        || (imageUrl.hostname === 'cdn.shopify.com' && imageUrl.pathname.indexOf('/s/files/') === 0);
      if (isShopifyImage) {
        image.sizes = '(min-width: 1760px) 550px, (min-width: 990px) 31.25vw, 62.5vw';
        imageSrcset = [360, 533, 720, 940, 1066, 1280, 1600].map(function (width) {
          var candidate = new URL(imageUrl.href);
          candidate.searchParams.set('width', String(width));
          return candidate.href + ' ' + width + 'w';
        }).join(', ');
      }
    } catch (error) {
      // Keep the original source if it is not a supported CDN URL.
    }
    if (window.MurallsViewportImages && typeof window.MurallsViewportImages.observe === 'function') {
      if (imageSrcset) image.dataset.viewportSrcset = imageSrcset;
      image.dataset.viewportSrc = product.image;
      image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    } else {
      if (imageSrcset) image.srcset = imageSrcset;
      image.src = product.image;
    }
    imageWrap.appendChild(image);

    // Catalog recommendations may not be on the current grid page. Resolve
    // their artwork only when Show pattern is used; an empty string means
    // the collection already confirmed that no artwork is available.
    if (product.patternImage !== '') {
      var pattern = document.createElement('img');
      pattern.className = 'collection-discovery__image card__pattern-image';
      pattern.alt = product.title + ' pattern artwork';
      pattern.loading = 'lazy';
      pattern.decoding = 'async';
      pattern.sizes = '(min-width: 1760px) 550px, (min-width: 990px) 31.25vw, 62.5vw';
      if (product.patternImage) pattern.dataset.patternSrc = product.patternImage;
      else pattern.dataset.patternProduct = product.handle;
      imageWrap.appendChild(pattern);
    }

    var content = document.createElement('span');
    content.className = 'collection-discovery__card-content';
    var type = document.createElement('span');
    type.className = 'collection-discovery__type';
    type.textContent = product.productType || 'Design';
    var title = document.createElement('span');
    title.className = 'collection-discovery__title';
    var editorialTitle = editorialCardTitle(product);
    if (editorialTitle.accent) {
      var boldTitle = document.createElement('span');
      boldTitle.className = 'collection-discovery__title-bold';
      boldTitle.textContent = editorialTitle.bold;
      var accentTitle = document.createElement('span');
      accentTitle.className = 'collection-discovery__title-accent';
      accentTitle.textContent = editorialTitle.accent + '.';
      title.append(boldTitle, document.createTextNode(' '), accentTitle);
    } else {
      title.textContent = editorialTitle.full;
    }
    var price = matchingPrice(grid, product.productType);
    content.append(type, title);
    if (price) content.appendChild(price);
    link.appendChild(imageWrap);
    card.appendChild(link);
    if (product.colorwaysMarkup) {
      var colors = document.createElement('template');
      colors.innerHTML = product.colorwaysMarkup;
      var colorwayRow = colors.content.querySelector('card-colorways');
      if (colorwayRow) card.appendChild(colorwayRow);
    }
    var details = document.createElement('a');
    details.className = 'collection-discovery__details-link';
    details.href = link.href;
    details.appendChild(content);
    card.appendChild(details);
    return card;
  }

  function discoveryPanel(grid, recommendations) {
    var existing = grid.parentNode.querySelector(':scope > .collection-discovery');
    var panel = existing || document.createElement('section');
    if (!existing) {
      panel.className = 'collection-discovery';
      panel.setAttribute('aria-label', 'Recommended designs');
      panel.innerHTML = '<div class="collection-discovery__header"><div class="collection-discovery__copy"><span class="collection-discovery__badge">For you</span><div><h2 class="collection-discovery__heading">Designs matched to your taste</h2><p class="collection-discovery__text">Based on designs you explored, with fresh finds mixed in.</p></div></div><div class="collection-discovery__actions"><button class="collection-discovery__button" type="button" data-discovery-remix>Mix again</button><button class="collection-discovery__button" type="button" data-discovery-reset>Reset</button></div></div><div class="collection-discovery__recommendations" data-discovery-recommendations></div>';
      grid.parentNode.insertBefore(panel, grid);
    }
    var list = panel.querySelector('[data-discovery-recommendations]');
    var revision = Number(panel.dataset.discoveryRender || 0) + 1;
    panel.dataset.discoveryRender = String(revision);
    function render(products) {
      if (!panel.isConnected || Number(panel.dataset.discoveryRender) !== revision) return;
      list.replaceChildren.apply(list, products.map(function (product) {
        return recommendationCard(product, grid);
      }));
      panel.classList.toggle('has-recommendations', products.length > 0);
      panel.classList.add('is-active');
      window.MurallsViewportImages?.observe(list);
    }
    if (recommendations.some(function (product) { return typeof product.colorwaysMarkup !== 'string'; })) {
      // Keep the current row while preparing complete cards, so colors never
      // appear later and push their titles down one by one.
      Promise.all(recommendations.map(loadColorways)).then(render);
    } else {
      render(recommendations);
    }
    return panel;
  }

  function applyOrder(grid, mix, reorder = true) {
    if (['new-arrivals', 'best-seller-wall-mural', 'best-seller-wallpaper'].includes(grid.dataset.discoveryCollectionHandle)) return false;
    var items = Array.from(grid.querySelectorAll(':scope > [data-discovery-card]'));
    if (!items.length || hasManualCollectionState()) return false;
    items.forEach(function (item, index) {
      if (!item.dataset.discoveryOriginal) item.dataset.discoveryOriginal = String(index);
    });
    var profile = cleanProfile();
    var ordered = composeOrder(items, profile, mix);
    var itemTypes = Array.from(new Set(items.map(function (item) { return item.dataset.discoveryProductType || ''; }).filter(Boolean)));
    var preferredType = itemTypes.length === 1 ? itemTypes[0] : '';
    var collectionProducts = items.map(collectionProductFromElement).filter(Boolean);
    var collectionHandles = new Set(collectionProducts.map(function (product) { return product.handle; }));
    var collectionHandle = String(grid.dataset.discoveryCollectionHandle || '');
    var broadCollection = /^(all|wall-murals?|wallpapers?|best-seller(?:-wall-mural|-wallpaper)?|new-arrivals?)$/i.test(collectionHandle);
    var interestInCollection = profile.interests.some(function (interest) {
      return collectionHandles.has(interest.handle);
    });
    var allowCatalogMatches = broadCollection || interestInCollection;
    var currentRecommendations = Array.from(grid.parentNode.querySelectorAll(':scope > .collection-discovery [data-discovery-recommendation]')).map(function (item) {
      return item.dataset.discoveryRecommendation || '';
    }).filter(Boolean);
    var recommendations = recommendationPool(profile, cacheStore(), mix, preferredType, currentRecommendations, collectionProducts, allowCatalogMatches);
    if (!ordered && !recommendations.length) return false;
    ordered = ordered || items;
    if (reorder) ordered.forEach(function (item, index) {
      item.style.setProperty('--animation-order', String(index + 1));
      grid.appendChild(item);
    });
    discoveryPanel(grid, recommendations);
    grid.dataset.discoveryApplied = '1';
    document.dispatchEvent(new CustomEvent('muralls:collection-reordered', { detail: { grid: grid } }));
    return true;
  }

  function observeSeen(grid) {
    if (seenObserver) seenObserver.disconnect();
    var timers = new WeakMap();
    seenObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var existing = timers.get(entry.target);
        if (existing) window.clearTimeout(existing);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.65) {
          timers.set(entry.target, window.setTimeout(function () {
            rememberSeen(entry.target.dataset.discoveryProductHandle || '');
          }, 1400));
        }
      });
    }, { threshold: [0.65] });
    grid.querySelectorAll(':scope > [data-discovery-card]').forEach(function (item) { seenObserver.observe(item); });
  }

  function initializeGrid() {
    var grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.discoveryReady === '1') return;
    grid.dataset.discoveryReady = '1';
    // Keep these collections in their catalog order, without a personalized row.
    if (['new-arrivals', 'best-seller-wall-mural', 'best-seller-wallpaper'].includes(grid.dataset.discoveryCollectionHandle)) {
      observeSeen(grid);
      return;
    }
    var mix = Number(readJson(MIX_KEY, {})[location.pathname] || 0);
    // The initial server-rendered row already reserves the final geometry.
    // Personalize only that row; never move a visible grid on arrival.
    if (cleanProfile().interests.length || !grid.parentNode.querySelector('[data-discovery-server]')) applyOrder(grid, mix, false);
    observeSeen(grid);

    // A customer can leave a product page before its background request finishes.
    // Retry from the collection page and repaint only the recommendation panel when
    // the precomputed Worker result becomes available.
    var profile = cleanProfile();
    var items = Array.from(grid.querySelectorAll(':scope > [data-discovery-card]'));
    var collectionHandles = new Set(items.map(function (item) {
      return item.dataset.discoveryProductHandle || '';
    }).filter(Boolean));
    var collectionHandle = String(grid.dataset.discoveryCollectionHandle || '');
    var broadCollection = /^(all|wall-murals?|wallpapers?|best-seller(?:-wall-mural|-wallpaper)?|new-arrivals?)$/i.test(collectionHandle);
    var interest = profile.interests.find(function (item) {
      if (!item.id || !item.productType) return false;
      if (collectionHandles.has(item.handle)) return true;
      return broadCollection && (!items.length || !item.productType || items.some(function (card) {
        return String(card.dataset.discoveryProductType || '').toLowerCase() === String(item.productType || '').toLowerCase();
      }));
    });
    if (interest && !hasCachedSimilar(interest)) {
      warmSimilar(interest).then(function () {
        if (!grid.isConnected) return;
        applyOrder(grid, mix, false);
      });
    }
  }

  function bindActions() {
    document.addEventListener('click', function (event) {
      var card = event.target.closest('[data-discovery-card]');
      if (card && event.target.closest('a')) {
        var product = productFromElement(card);
        rememberInterest(product, 2);
      }

      var recommendation = event.target.closest('[data-discovery-recommendation]');
      if (recommendation && event.target.closest('a')) {
        var profile = cleanProfile();
        var suggested = recommendation.dataset.discoveryRecommendation || '';
        var cached = cacheStore();
        var matched = null;
        profile.interests.some(function (interest) {
          var entry = cached[interest.handle];
          matched = entry && Array.isArray(entry.products) ? entry.products.find(function (item) { return item.handle === suggested; }) : null;
          return Boolean(matched);
        });
        if (matched) rememberInterest({ handle: matched.handle, image: matched.image, title: matched.title, productType: matched.productType }, 2);
      }

      var remix = event.target.closest('[data-discovery-remix]');
      if (remix) {
        event.preventDefault();
        var grid = document.getElementById('product-grid');
        if (!grid) return;
        var scrollPosition = window.scrollY;
        var mixes = readJson(MIX_KEY, {});
        mixes[location.pathname] = Number(mixes[location.pathname] || 0) + 1;
        writeJson(MIX_KEY, mixes);
        applyOrder(grid, mixes[location.pathname]);
        window.requestAnimationFrame(function () {
          window.scrollTo({ top: scrollPosition, left: window.scrollX, behavior: 'auto' });
        });
      }

      var reset = event.target.closest('[data-discovery-reset]');
      if (reset) {
        var resetGrid = document.getElementById('product-grid');
        var profile = cleanProfile();
        profile.interests = [];
        writeJson(PROFILE_KEY, profile);
        if (resetGrid) {
          restoreOrder(resetGrid);
          resetGrid.dataset.discoveryApplied = '0';
          var resetPanel = resetGrid.parentNode.querySelector(':scope > .collection-discovery');
          if (resetPanel) {
            resetPanel.dataset.discoveryRender = String(Number(resetPanel.dataset.discoveryRender || 0) + 1);
            resetPanel.classList.remove('is-active');
          }
        }
      }

      var strongSignal = event.target.closest('[data-wv-open], [data-complete-room-open], form[action*="/cart/add"] button[type="submit"], .product-form__submit');
      if (strongSignal && pageProduct) {
        rememberInterest(pageProduct, strongSignal.matches('[data-complete-room-open], form[action*="/cart/add"] button[type="submit"], .product-form__submit') ? 4 : 3);
        warmSimilar(pageProduct);
      }
    }, true);
  }

  function initializeProduct() {
    var reference = document.querySelector('[data-visual-search-image][data-visual-search-handle]');
    pageProduct = productFromElement(reference);
    if (!pageProduct) return;

    // A product landing is a useful but tentative taste signal. Prepare its
    // embedding matches immediately so short visits from ads and Pinterest
    // can still personalize the next collection view.
    rememberInterest(pageProduct, 0.35);
    const prepare = () => {
      if (navigator.connection?.saveData) return;
      if ('requestIdleCallback' in window) requestIdleCallback(() => warmSimilar(pageProduct), { timeout: 5000 });
      else setTimeout(() => warmSimilar(pageProduct), 1500);
    };
    if (document.readyState === 'complete') prepare();
    else window.addEventListener('load', prepare, { once: true });

    window.setTimeout(function () {
      if (document.visibilityState !== 'visible') return;
      rememberInterest(pageProduct, 0.65);
    }, 3500);
  }

  function initialize() {
    initializeProduct();
    initializeGrid();
    bindActions();
    var container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(function () { initializeGrid(); }).observe(container, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
